import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BokkzWageDataService,
  ContractGegevens,
  ContractGegevensAardArbeidsverhouding,
  ContractGegevensFaseIndeling,
  ContractGegevensInvloedVerzekeringsplicht,
  ContractGegevensSoortInkomstenverhouding,
  ContractGegevensVerzekeringssituatieZvw,
  Dienstverband,
  Periode,
  WerknemerEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiTabModule } from '@snelstart/ui-tab';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiIconModule } from '@snelstart/ui-icon';
import { debounceTime, filter, merge, Observable, shareReplay, Subscription, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type ContractGegevensFormGroup = FormGroup<{
  periode: PeriodeFormGroup;
  soortInkomstenverhouding: FormControl<ContractGegevensSoortInkomstenverhouding | null>;
  aardArbeidsverhouding: FormControl<ContractGegevensAardArbeidsverhouding | null>;
  verzekeringssituatieZvw: FormControl<ContractGegevensVerzekeringssituatieZvw | null>;
  invloedVerzekeringsplicht: FormControl<ContractGegevensInvloedVerzekeringsplicht | null>;
  faseIndeling: FormControl<ContractGegevensFaseIndeling | null>;
  schriftelijkeOvereenkomst: FormControl<boolean | null>;
  bepaaldeTijd: FormControl<boolean | null>;
  oproepovereenkomst: FormControl<boolean | null>;
  jaarurennorm: FormControl<boolean | null>;
}>;

type LijstFormGroup = FormGroup<{
  contractGegevens: FormArray<ContractGegevensFormGroup>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-contract',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiIconModule
  ],
  templateUrl: './contract.component.html',
  styleUrl: './contract.component.scss'
})
export class ContractComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabDv = 0;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzWageDataService);
  private werknemerEntity = {} as WerknemerEntity;

  public lijstFg: LijstFormGroup = new FormGroup({
    contractGegevens: new FormArray<ContractGegevensFormGroup>([])
  });

  public index = 0;
  public dialogVisible = false;
  private saveSubscription?: Subscription;
  private recalculationSubscription?: Subscription;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Ja' },
    { value: false, label: 'Nee' }
  ];

  public optionsJaNeeBepaaldeTijd: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Bepaalde tijd' },
    { value: false, label: 'Onbepaalde tijd' }
  ];

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      tap((data) => {
        this.werknemerEntity = data;
        if (data.werknemerGegevens?.dienstverbanden?.at(0)) {
          this.updateForm(data.werknemerGegevens?.dienstverbanden[0].contractGegevensList ?? []);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  ngOnInit(): void {
    this.bindContractChangeHandlers();
  }

  ngOnDestroy(): void {
    this.saveSubscription?.unsubscribe();
    this.recalculationSubscription?.unsubscribe();
    this.saveForm();
  }

  get contractGegevens(): FormArray<ContractGegevensFormGroup> {
    return this.lijstFg.controls.contractGegevens;
  }

  addRij(): void {
    const length = this.contractGegevens.length;
    let newDate = new Date("2026-12-31");
    if (length > 0) {
      const lastRow = this.contractGegevens.at(length - 1);
      const laatsteEinddatum = new Date(lastRow.controls.periode.controls.einddatum.value!);
      newDate = new Date(laatsteEinddatum);
      newDate.setDate(laatsteEinddatum.getDate() + 1);
    }
    const nieuweContractGegevens = {
      periode: {
        begindatum: newDate
      },
      soortInkomstenverhouding: this.contractGegevens.at(length - 1).controls.soortInkomstenverhouding.value,
      aardArbeidsverhouding: this.contractGegevens.at(length - 1).controls.aardArbeidsverhouding.value,
      verzekeringssituatieZvw: this.contractGegevens.at(length - 1).controls.verzekeringssituatieZvw.value,
      invloedVerzekeringsplicht: this.contractGegevens.at(length - 1).controls.invloedVerzekeringsplicht.value,
      faseIndeling: this.contractGegevens.at(length - 1).controls.faseIndeling.value,
      bepaaldeTijd: this.contractGegevens.at(length - 1).controls.bepaaldeTijd.value,
      schriftelijkeOvereenkomst: this.contractGegevens.at(length - 1).controls.schriftelijkeOvereenkomst.value,
      oproepovereenkomst: this.contractGegevens.at(length - 1).controls.oproepovereenkomst.value,
      jaarurennorm: this.contractGegevens.at(length - 1).controls.jaarurennorm.value,
    } as ContractGegevens;
    this.contractGegevens.push(this.createFormGroup(nieuweContractGegevens));
    this.bindContractChangeHandlers();
    this.saveForm();
  }

  verwijderRijContract(index: number): void {
    if (index > 0) {
      this.contractGegevens.at(index - 1).controls.periode.patchValue({ einddatum: undefined });
    }
    this.contractGegevens.removeAt(index);
    this.contractGegevens.updateValueAndValidity();
    this.bindContractChangeHandlers();
    this.saveForm();
  }

  private createFormGroup(item?: ContractGegevens): ContractGegevensFormGroup {
    return new FormGroup({
      periode: this.createPeriodeFormGroup(item?.periode),
      soortInkomstenverhouding: new FormControl<ContractGegevensSoortInkomstenverhouding | null>(
        item?.soortInkomstenverhouding ?? ContractGegevensSoortInkomstenverhouding.LOON_SALARIS_DGA_NIET_VERZEKERD
      ),
      aardArbeidsverhouding: new FormControl<ContractGegevensAardArbeidsverhouding | null>(
        item?.aardArbeidsverhouding ?? null
      ),
      verzekeringssituatieZvw: new FormControl<ContractGegevensVerzekeringssituatieZvw | null>(
        item?.verzekeringssituatieZvw ?? null
      ),
      invloedVerzekeringsplicht: new FormControl<ContractGegevensInvloedVerzekeringsplicht | null>(
        item?.invloedVerzekeringsplicht ?? null
      ),
      faseIndeling: new FormControl<ContractGegevensFaseIndeling | null>(item?.faseIndeling ?? null),
      schriftelijkeOvereenkomst: new FormControl<boolean | null>(item?.schriftelijkeOvereenkomst ?? null),
      bepaaldeTijd: new FormControl<boolean | null>(item?.bepaaldeTijd ?? null),
      oproepovereenkomst: new FormControl<boolean | null>(item?.oproepovereenkomst ?? null),
      jaarurennorm: new FormControl<boolean | null>(item?.jaarurennorm ?? null),
    });
  }

  private createPeriodeFormGroup(periode?: Periode): PeriodeFormGroup {
    return new FormGroup({
      begindatum: new FormControl<Date | null>(periode?.begindatum ?? null),
      einddatum: new FormControl<Date | null>(periode?.einddatum ?? null),
    });
  }

  private updateForm(contractgegevens : ContractGegevens[]): void {
    const lijst = contractgegevens || [];
    const formArray = this.contractGegevens;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], { emitEvent: false });
      } else {
        formArray.push(this.createFormGroup(lijst[i]));
      }
    }

    this.bindContractChangeHandlers();
  }

  private bindContractChangeHandlers(): void {
    this.saveSubscription?.unsubscribe();
    this.recalculationSubscription?.unsubscribe();

    const einddatumStreams = this.contractGegevens.controls.map(control =>
      control.controls.periode.controls.einddatum.valueChanges
    );

    const bepaaldeTijdStreams = this.contractGegevens.controls.map(control =>
      control.controls.bepaaldeTijd.valueChanges
    );
    const schriftelijkeOvereenkomstStreams = this.contractGegevens.controls.map(control =>
      control.controls.schriftelijkeOvereenkomst.valueChanges
    );
    const oproepovereenkomstStreams = this.contractGegevens.controls.map(control =>
      control.controls.oproepovereenkomst.valueChanges
    );

    const recalcStreams = [
      ...bepaaldeTijdStreams,
      ...schriftelijkeOvereenkomstStreams,
      ...oproepovereenkomstStreams,
    ];

    if (!recalcStreams.length) {
      this.recalculationSubscription = undefined;
    }

    if (!einddatumStreams.length) {
      this.saveSubscription = undefined;
    } else {
      this.saveSubscription = merge(...einddatumStreams)
        .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.saveForm();
        });
    }

    if (recalcStreams.length) {
      this.recalculationSubscription = merge(...recalcStreams)
        .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.saveForm();
          this.dataService
            .calculateLatestWerknemerEntity()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        });
    }
  }

  public saveForm(): void {
    const contractGegevensList = this.lijstFg.getRawValue().contractGegevens;

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current?.werknemerGegevens ? current : this.werknemerEntity;
      const eersteDienstverband = baseEntity.werknemerGegevens?.dienstverbanden?.at(0);

      if (!baseEntity.werknemerGegevens || !eersteDienstverband) {
        return baseEntity;
      }

      const dienstverband = {
        ...eersteDienstverband,
        dga: false,
        contractGegevensList,
      } as Dienstverband;

      const nextWerknemer = {
        ...baseEntity,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          dienstverbanden: [dienstverband],
        },
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }

  public checkEinddatum(): boolean {
    const length = this.contractGegevens.length;
    const waarde = this.contractGegevens.at(length - 1).controls.periode.controls.einddatum.value;
    return !!waarde;
  }
}
